import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Navigate } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
  AnimatedHeadline,
  CuratedUniverse,
  EchoHeadline,
  FaqSection,
  FinalCta,
  HeroBackdrop,
  HEADLINE_FROM,
  HEADLINE_TO,
  HowWonderWorks,
  TrustedBy,
  useSectionReveal,
} from '@/components/marketing';
import { Glow, Icon, LinkButton } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { dashboardFor, paths } from '@/routes/paths';

gsap.registerPlugin(ScrollTrigger);

export function LandingPage() {
  const { status, isAuthenticated, user } = useAuth();
  // The marketing page is for visitors. Someone signed in goes straight to
  // their own dashboard, whether they typed the address or were sent here.
  // Until the session is known, show nothing rather than flash the page.
  if (status === 'initialising') return null;
  if (isAuthenticated) return <Navigate to={dashboardFor(user?.role)} replace />;

  return (
    <>
      <Hero />
      <HowWonderWorks />
      <PerfectMatch />
      <CuratedUniverse />
      <TrustedBy />
      <FaqSection />
      <FinalCta />
    </>
  );
}

function Hero() {
  // The entrance, played once per page load and never reversed. The scroll-away
  // is `wonder-hero-zoom`, a CSS scroll-driven animation on the content
  // wrapper; nothing here touches that element, so the two never share a
  // transform. Only transforms and opacity move.
  //
  //   0.00  the backdrop settles in behind, slower than the type
  //   0.20  each headline line rises out of its mask, 0.12s apart, over the
  //         slow three seconds every heading on the page takes
  //   1.40  the buttons and the subline follow, softly
  //
  // `fromTo` writes the start values the moment the timeline is built, in a
  // layout effect, so the first paint is already the hidden state and nothing
  // jumps when the trigger fires.
  const root = useSectionReveal(
    ({ reveal }) => {
      reveal(
        '.js-hero-backdrop',
        { scale: 1.05, opacity: 0, y: 12 },
        { duration: 1.8, ease: 'power2.out' },
        0,
      );
      // "Internships", then "made easy." on the shared beat -- a touch faster
      // than the shared rise, since this is the first thing on the page.
      reveal('.js-headline-line', HEADLINE_FROM, { ...HEADLINE_TO, duration: 2.2 }, 0.2);
      reveal(
        '.js-hero-secondary',
        { y: 24, opacity: 0 },
        { duration: 0.9, stagger: 0.1, ease: 'power3.out' },
        1.4,
      );
    },
    { start: 'top 80%', once: true },
  );

  // The hand-over, with the hero held still for most of a screen of scroll:
  // the headline grows a little and fades (the CSS zoom on its wrapper, gone
  // by 45vh), the line at the foot fades with it, and the footage is left on
  // its own for a beat. Then the pin lets go and the hero scrolls away as
  // any section does, with the next one following it up -- the two never
  // overlap.
  useLayoutEffect(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.to(gsap.utils.selector(root.current)('.js-hero-foot'), {
        opacity: 0,
        ease: 'none',
        scrollTrigger: { trigger: root.current, start: 'top top', end: '+=30%', scrub: true },
      });

      // The footage pushes in over the whole hold, carrying the reader on
      // into the next section rather than sitting still behind the fade.
      gsap.to(gsap.utils.selector(root.current)('.js-hero-stage'), {
        scale: 1.5,
        transformOrigin: '50% 40%',
        ease: 'none',
        // Through the hold and on while the hero scrolls away, so the push-in
        // is still moving as the next section comes up under it.
        scrollTrigger: {
          trigger: root.current,
          start: 'top top',
          end: () => `+=${window.innerHeight * 1.5}`,
          scrub: true,
          invalidateOnRefresh: true,
        },
      });

      ScrollTrigger.create({
        trigger: root.current,
        start: 'top top',
        // Lets go as the headline finishes fading, so the footage is never
        // left alone for a screen: the next section starts rising from the
        // bottom straight away.
        end: '+=50%',
        pin: true,
      });
    });
    return () => media.revert();
  }, [root]);

  return (
    <section
      ref={root}
      className="relative isolate flex min-h-dvh flex-col overflow-hidden pt-28 pb-12 lg:pt-32"
    >
      {/* A wrapper for the scroll zoom, so it never shares a transform with
          the backdrop's own entrance. */}
      <div className="js-hero-stage absolute inset-0">
        <HeroBackdrop className="js-hero-backdrop" />
      </div>

      <div className="wonder-container wonder-hero-zoom relative flex flex-1 flex-col justify-center text-center">
        <AnimatedHeadline lines={[['Internships', 'made easy.']]} />

        <div className="js-hero-secondary mt-10 flex flex-wrap justify-center gap-4">
          <LinkButton to={paths.registerStudent} variant="outlineLight" shape="pill" size="pillMd">
            <Icon name="student" size={17} />
            Join As Student
            <Icon name="arrowRight" size={16} />
          </LinkButton>
          <LinkButton to={paths.registerCompany} variant="light" shape="pill" size="pillMd">
            <Icon name="company" size={17} />
            Hire Talent
            <Icon name="arrowRight" size={16} />
          </LinkButton>
        </div>
      </div>

      <div className="js-hero-foot relative">
        <p className="js-hero-secondary wonder-container text-on-night-muted mt-12 text-center text-lg">
          Built for startup founders and ambitious students — Find your fit.
        </p>
      </div>
    </section>
  );
}

function PerfectMatch() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const scope = root.current;
    const media = gsap.matchMedia();

    // The markup already renders where it belongs, so honouring the preference
    // means declining to animate rather than animating to the same place.
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const select = gsap.utils.selector(scope);
      // Spans the whole crossing -- from the section's top edge entering the
      // bottom of the viewport to its bottom edge leaving the top -- so the
      // travel is spread over every pixel the section is visible for rather
      // than finishing early and sitting still.
      const scrollTrigger = {
        trigger: scope,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 0.6,
      };

      // `ease: 'none'` on both: the scrollbar is the ease. Anything else makes
      // the type appear to speed up and slow down under a steady scroll.
      gsap.fromTo(
        select('.js-match-type'),
        { yPercent: 14, scale: 0.94 },
        { yPercent: -14, scale: 1.06, ease: 'none', scrollTrigger },
      );
      gsap.fromTo(
        select('.js-match-glow'),
        { yPercent: -26, scale: 0.8 },
        { yPercent: 26, scale: 1.15, ease: 'none', scrollTrigger },
      );
    });

    return () => media.revert();
  }, []);

  return (
    <section ref={root} className="wonder-screen relative overflow-x-clip py-14 lg:py-20">
      <div>
        <Glow
          className="js-match-glow left-1/2 -bottom-40 -translate-x-1/2"
          size={820}
          opacity={0.16}
        />
        <div className="relative">
          <EchoHeadline className="js-match-type" text="Meet your perfect match" />
        </div>
      </div>
    </section>
  );
}
